// `defineBatch` — the validator every batch record runs through, the sibling of
// `defineModel`. A batch is the production run a model was generated in (see
// {@link Batch}); the set of batches is small and lives in `src/batches`. This
// keeps that set from drifting: a malformed batch fails loudly at load time
// (and in the registry test) with a teaching error, the same way a malformed
// model does.

import type { Batch } from "./types";

/** What an author passes to {@link defineBatch}. Mirrors {@link Batch}. */
export interface BatchInput {
  id: string;
  label: string;
  for: string;
  generator: string;
  style: string;
  description?: string;
  date?: string;
}

const KEBAB = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function fail(id: string, what: string, fix: string): never {
  throw new Error(`Invalid batch "${id}": ${what}. ${fix}`);
}

/**
 * Validate and normalise a batch. Throws a teaching error (what is wrong + how
 * to fix it) on any violation, so a bad batch surfaces at the seam, never as a
 * model silently pointing at provenance that does not exist.
 */
export function defineBatch(input: BatchInput): Batch {
  const { id } = input;
  if (!id || !KEBAB.test(id)) {
    fail(id || "(missing)", "id must be non-empty kebab-case", 'Use lowercase words joined by "-", e.g. "45-optimized".');
  }
  if (!input.label?.trim()) fail(id, "label is required", "Add a human display name, e.g. \"45 Optimized\".");
  if (!input.for?.trim()) {
    fail(id, "for is required", 'Name the game or purpose this batch was generated for, e.g. "Age of MCP".');
  }
  if (!input.generator?.trim()) {
    fail(id, "generator is required", 'Name the generating model, or "unknown" if it was a human or not recorded.');
  }
  if (!input.style?.trim()) fail(id, "style is required", "Describe the style brief this batch was generated under.");

  return {
    id,
    label: input.label.trim(),
    for: input.for.trim(),
    generator: input.generator.trim(),
    style: input.style.trim(),
    description: input.description?.trim(),
    date: input.date?.trim(),
  };
}
